import branch from "branch-sdk";

/**
 * Initialize Branch SDK with environment configuration
 * @param {Object} options - Optional Branch initialization options
 * @returns {Promise<Object>} - Promise that resolves with Branch init data
 */
export const initBranch = async (options = {}) => {
  const branchKey = import.meta.env.VITE_BRANCH_KEY;

  if (!branchKey) {
    const error = new Error("VITE_BRANCH_KEY environment variable is not set");
    console.error("Branch initialization failed:", error.message);
    throw error;
  }

  // Default options for better user experience
  const defaultOptions = {
    // Disable journeys by default to prevent unexpected popups
    no_journeys: false,
    // Add nonce for CSP if available
    ...(import.meta.env.VITE_CSP_NONCE && {
      nonce: import.meta.env.VITE_CSP_NONCE,
    }),
    ...options,
  };

  return new Promise((resolve, reject) => {
    branch.init(branchKey, defaultOptions, (err, data) => {
      if (err) {
        console.error("Branch initialization error:", err);
        reject(err);
      } else {
        console.log("Branch initialized successfully:", {
          hasApp: data?.has_app,
          identity: data?.identity,
          referringLink: data?.referring_link,
        });
        resolve(data);
      }
    });
  });
};

/**
 * Create a deep link with Branch
 * @param {Object} linkData - Data to associate with the link
 * @param {Object} linkOptions - Link configuration options
 * @returns {Promise<string>} - Promise that resolves with the generated link
 */
export const createBranchLink = (linkData = {}, linkOptions = {}) => {
  return new Promise((resolve, reject) => {
    branch.link(linkData, linkOptions, (err, link) => {
      if (err) {
        console.error("Branch link creation error:", err);
        reject(err);
      } else {
        resolve(link);
      }
    });
  });
};

/**
 * Track a custom event with Branch
 * @param {string} eventName - Name of the event to track
 * @param {Object} eventData - Data associated with the event
 * @returns {Promise<void>}
 */
export const trackBranchEvent = (eventName, eventData = {}) => {
  return new Promise((resolve, reject) => {
    try {
      branch.track(eventName, eventData, (err) => {
        if (err) {
          console.error(`Branch event tracking error for "${eventName}":`, err);
          reject(err);
        } else {
          console.log(`Branch event "${eventName}" tracked successfully`);
          resolve();
        }
      });
    } catch (error) {
      console.error(`Branch event tracking failed for "${eventName}":`, error);
      reject(error);
    }
  });
};

/**
 * Set user identity in Branch
 * @param {string} userId - Unique identifier for the user
 * @returns {Promise<void>}
 */
export const setBranchIdentity = (userId) => {
  return new Promise((resolve, reject) => {
    if (!userId) {
      const error = new Error("User ID is required");
      reject(error);
      return;
    }

    branch.setIdentity(userId, (err, data) => {
      if (err) {
        console.error("Branch set identity error:", err);
        reject(err);
      } else {
        console.log("Branch identity set successfully:", data);
        resolve(data);
      }
    });
  });
};

/**
 * Clear user identity in Branch
 * @returns {Promise<void>}
 */
export const clearBranchIdentity = () => {
  return new Promise((resolve, reject) => {
    branch.logout((err) => {
      if (err) {
        console.error("Branch logout error:", err);
        reject(err);
      } else {
        console.log("Branch identity cleared successfully");
        resolve();
      }
    });
  });
};

// Export the branch instance as default for direct access
export { branch };

/**
 * Branch plugin for Vue app
 * This will be automatically registered by the registerPlugins function
 *
 * @param {App} app - Vue app instance
 */
export default function (app) {
  // Make Branch available globally in the app
  app.config.globalProperties.$branch = branch;

  // Provide Branch functions as composables
  app.provide("branch", {
    initBranch,
    createBranchLink,
    trackBranchEvent,
    setBranchIdentity,
    clearBranchIdentity,
    instance: branch,
  });

  // Auto-initialize Branch if key is available
  if (import.meta.env.VITE_BRANCH_KEY) {
    initBranch().catch((err) => {
      console.warn("Branch auto-initialization failed:", err.message);
    });
  }
}
